import { useEffect, useRef, useState } from 'react'
import { useStyletron } from 'baseui'
import { useTranslation } from 'react-i18next'
import { ProviderOverrides } from './styled-components'
import { ChevronDown, ChevronUp } from 'baseui/icon'
import { useSelector } from 'react-redux'
import { selectPageConfigurationData } from '@/store/PageConfiguration/PageConfigurationSelectors'
import { selectAllAdditionalSalesData } from '../SubscriptionFormSelectors'
import {
  selectAdditionalSaleModuleConfigurationData,
  selectCommonFormConfigurationData,
} from '../SubscriptionFormConfigurationSelectors'
import useFormStatus from '@/containers/SubscriptionFormPage/useFormStatus'
import retrieveAdditionalSalesIcons from '../../../shared/icons/retrieveAdditionalSalesIcons'
import useAdditionalSaleFormData from './useAdditionalSaleFormData'
import useBusinessType from '@/shared/hooks/useBusinessType'
import * as Sentry from '@sentry/react'

const useAdditionalSalesContainer = () => {
  const { t } = useTranslation()
  const [, theme] = useStyletron()
  const [activeTab, setActiveTab] = useState('leadAssistance')
  const containerRef = useRef()
  const { type: applicationType } = useBusinessType({
    loaderRoutesEnergy: {},
    loaderRoutes: {},
    loaderRoutesInsurance: {},
    modulesStatuses: {},
    modulesStatusesEnergy: {},
    modulesStatusesInsurance: {},
  })
  const additionalSales = useSelector(selectAllAdditionalSalesData)
  const { id } = useSelector(selectCommonFormConfigurationData)
  const { tabToShow, shouldScroll } = useSelector(selectAdditionalSaleModuleConfigurationData)
  const { country, additionalSalesStatuses } = useSelector(selectPageConfigurationData)
  const selectedSales = Object.keys(additionalSales).filter(key => additionalSales[key]?.zohoId)
  const isAvailableAdditionalSale = key => {
    return additionalSalesStatuses[key] && additionalSalesStatuses[key] === true
  }
  const { updateLead } = useAdditionalSaleFormData()
  const hasZohoIdAdditionalSale = key => {
    return additionalSales[key]?.zohoId || additionalSales[key]?.zoho_id
  }

  const vaPrioritiesKeys = [
    'leadAssistance',
    'leadCarbonOffset',
    'leadProtected',
    'leadVoltalisBizdev',
    'leadMonConseillerPerso',
    'leadAccessoriesSfr',
    'leadAxaSmartphone',
    'leadFloraAssuranceEthias',
    'leadFloraAssuranceLead',
    'leadYagoAssuranceHouse',
    'leadYagoAssuranceCar',
    'leadYagoAssuranceMoto',
    'leadSantevetAssurance',
    'leadVerisureTelecomBe',
    'leadBoilerEneco',
    'leadBoilerEngie',
    'leadBoilerHomeserve',
    'leadTelecomSat',
  ]
  const leadPrioritiesKeys = [
    'leadTelecom',
    'leadTelecomLovys',
    'leadAssurance',
    'leadEthias',
    'leadVerisure',
    'leadAssuranceEmprunteur',
    'leadTelecomEnergy',
    'leadBankinAssurance',
    'leadElite',
    'leadAssuranceDefault',
    'leadCentury21',
  ]

  if (applicationType === 'energie') {
    const protectedIndex = vaPrioritiesKeys.indexOf('leadProtected')
    if (protectedIndex !== -1) {
      vaPrioritiesKeys.splice(protectedIndex, 1)
    }
  }

  const othersKeys = Object.keys(additionalSales).filter(
    key => !vaPrioritiesKeys.includes(key) && !leadPrioritiesKeys.includes(key),
  )
  const [showLeadPriorities, setShowLeadPriorities] = useState(true)
  const [showVaPriorities, setShowVaPriorities] = useState(true)
  const [showOthers, setShowOthers] = useState(false)
  const renderTabTitle = (text, isVisible) => {
    const arrowIcon = isVisible ? <ChevronDown size={24} /> : <ChevronUp size={24} />

    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {text}
        {arrowIcon}
      </div>
    )
  }

  const { formIsLoaded, zohoIsInValid } = useFormStatus()
  const hideComponent = () => {
    return !formIsLoaded || !country || zohoIsInValid || country === 'fr'
  }
  const hasValue = object => {
    return object && Object.values(object).some(value => value !== null && value !== '' && value !== false)
  }

  const getLogoColor = key => {
    if (additionalSales[key]?.zohoId) {
      return theme.colors.positive
    }
    if (hasValue(additionalSales[key])) {
      return theme.colors.warning
    }

    return activeTab === key ? theme.colors.primary : theme.colors.mono800
  }

  const getSaleIcon = key => {
    return retrieveAdditionalSalesIcons(key, 20, ProviderOverrides(), getLogoColor(key))
  }

  const getSaleTab = (key, Icon) => (
    <>
      {Icon}
      <div dangerouslySetInnerHTML={{ __html: '&nbsp;' + t('additionalSales:titles.' + key) }} />
    </>
  )

  const getSaleConfig = key => {
    switch (key) {
      case 'leadAssistance':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadAssistance')),
          filePath: 'LeadAssistanceSale',
        }
      case 'leadTelecom':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadTelecom')),
          filePath: 'LeadTelecomSale',
        }
      case 'leadTelecomLovys':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadTelecomLovys')),
          filePath: 'LeadTelecomLovysSale',
        }
      case 'leadCarbonOffset':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadCarbonOffset')),
          filePath: 'LeadCarbonOffsetSale',
        }
      case 'leadGaranka':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadGaranka')),
          filePath: 'LeadGarankaSale',
        }
      case 'leadProxiserve':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadProxiserve')),
          filePath: 'LeadProxiserve',
        }
      case 'leadAssurance':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadAssurance')),
          filePath: 'LeadAssuranceSale',
        }
      case 'leadAssuranceDefault':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadAssuranceDefault')),
          filePath: 'LeadAssuranceDefaultSale',
        }
      case 'leadAssuranceManda':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadAssuranceDefault')),
          filePath: 'LeadAssuranceMandaSale',
        }
      case 'leadVoltalisBizdev':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadVoltalis')),
          filePath: 'LeadVoltalisBizdevSale',
        }
      case 'leadNeat':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadNeat')),
          filePath: 'LeadNeatSale',
        }
      case 'leadCentury21':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadCentury21')),
          filePath: 'LeadCentury21Sale',
        }
      case 'leadMovingNextories':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadMovingNextories')),
          filePath: 'LeadMovingNextoriesSale',
        }
      case 'leadYouStock':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadYouStock')),
          filePath: 'LeadYouStockSale',
        }
      case 'leadFloraAssuranceEthias':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadFloraAssurance')),
          filePath: 'LeadFloraAssuranceEthiasSale',
        }
      case 'leadFloraAssuranceLead':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadFloraAssurance')),
          filePath: 'LeadFloraAssuranceLeadSale',
        }
      case 'leadSantevetAssurance':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadSantevetAssurance')),
          filePath: 'LeadSantevetAssuranceSale',
        }
      case 'leadEthias':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadEthias')),
          filePath: 'LeadEthias',
        }
      case 'leadElite':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadElite')),
          filePath: 'LeadEliteSale',
        }
      case 'leadVerisureTelecomBe':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadVerisureTelecomBe')),
          filePath: 'LeadVerisureTelecomBeSale',
        }
      case 'leadBoilerEneco':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadBoilerEneco')),
          filePath: 'LeadBoilerEnecoSale',
        }
      case 'leadBoilerEngie':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadBoilerEngie')),
          filePath: 'LeadBoilerEngieSale',
        }
      case 'leadBoilerHomeserve':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadBoilerHomeserve')),
          filePath: 'LeadBoilerHomeserveSale',
        }
      case 'leadYagoAssuranceHouse':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadYagoAssurance')),
          filePath: 'LeadYagoAssuranceHouseSale',
        }
      case 'leadYagoAssuranceCar':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadYagoAssurance')),
          filePath: 'LeadYagoAssuranceCarSale',
        }
      case 'leadYagoAssuranceMoto':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadYagoAssurance')),
          filePath: 'LeadYagoAssuranceMotoSale',
        }
      case 'leadBankinAssurance':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadBankinAssurance')),
          filePath: 'LeadBankinAssuranceSale',
        }
      case 'leadPartnership':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadPartnership')),
          filePath: 'LeadPartnershipSale',
        }
      case 'leadWater':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadWater')),
          filePath: 'WaterInteraction',
          vaType: 'interactions',
        }
      case 'leadMesAllocs':
        return {
          title: getSaleTab(key, getSaleIcon('sales-mesAllocs')),
          filePath: 'LeadMesAllocsSale',
        }
      case 'leadGroupPurchaseTelecom':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadGroupPurchaseTelecom')),
          filePath: 'LeadGroupPurchaseTelecom',
        }
      case 'leadAssistancePro':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadAssistancePro')),
          filePath: 'LeadAssistanceProSale',
        }
      case 'leadMonConseillerPerso':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadMonConseillerPerso')),
          filePath: 'LeadMonConseillerPersoSale',
        }
      case 'leadAssuranceEmprunteur':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadAssuranceEmprunteur')),
          filePath: 'LeadAssuranceEmprunteur',
          isBlockedIfMcpCreated: true,
        }
      case 'leadVerisure':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadVerisure')),
          filePath: 'LeadVerisure',
          isBlockedIfMcpCreated: true,
        }
      case 'leadTucoenergie':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadTucoenergie')),
          filePath: 'LeadTucoenergie',
        }
      case 'leadEnergygo':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadEnergygo')),
          filePath: 'LeadEnergygo',
        }
      case 'leadOffshoreTelecom':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadOffshoreTelecom')),
          filePath: 'LeadOffshoreTelecom',
        }
      case 'leadAssurancePro':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadAssurancePro')),
          filePath: 'LeadAssurancePro',
        }
      case 'leadVerisureTelecom':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadVerisureTelecom')),
          filePath: 'VerisureTelecomSale',
        }
      case 'leadProtected': {
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadProtected')),
          filePath: 'ProtectedSale',
        }
      }
      case 'leadProtectedClub': {
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadProtectedClub')),
          filePath: 'ProtectedClubSale',
        }
      }
      case 'leadTelecomEnergy': {
        return {
          title: getSaleTab(key, getSaleIcon('sales-telecomEnergy')),
          filePath: 'TelecomEnergySale',
        }
      }
      case 'leadTelecomSat': {
        return {
          title: getSaleTab(key, getSaleIcon('sales-telecomSat')),
          filePath: 'TelecomSatSale',
        }
      }
      case 'leadAccessoriesSfr': {
        return {
          title: getSaleTab(key, getSaleIcon('sales-accessoriesSfr')),
          filePath: 'AccessoriesSfrSale',
        }
      }
      case 'leadEnergyProPlaceDesEnergies': {
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadEnergyProPlaceDesEnergies')),
          filePath: 'LeadEnergyProPlaceDesEnergies',
        }
      }
      case 'leadAxaSmartphone': {
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadAxaSmartphone')),
          filePath: 'LeadAxaSmartphoneSale',
        }
      }
      case 'leadHomyos': {
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadHomyos')),
          filePath: 'LeadHomyosSale',
        }
      }
      case 'leadAssuranceEmprunteurMeilleurTaux': {
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadAssuranceEmprunteurMeilleurTaux')),
          filePath: 'LeadAssuranceEmprunteurMeilleurTauxSale',
        }
      }
      case 'leadEddyProprietaire': {
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadEddyProprietaire')),
          filePath: 'LeadEddyProprietaire',
        }
      }
      case 'leadBankPanorabanques':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadBankPanorabanques')),
          filePath: 'LeadBankPanorabanques',
        }
      case 'leadMutuelleSantiane':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadMutuelleSantiane')),
          filePath: 'LeadMutuelleSantiane',
        }
      case 'leadYolo':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadYolo')),
          filePath: 'LeadYoloSale',
        }
      case 'leadTim':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadTim')),
          filePath: 'LeadTimSale',
        }
      case 'leadIren':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadIren')),
          filePath: 'LeadIrenSale',
        }
      case 'leadFastWeb':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadFastWeb')),
          filePath: 'LeadFastWebSale',
        }
      case 'leadProtectedIt':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadProtectedIt')),
          filePath: 'LeadProtectedItSale',
        }
      case 'leadEdison':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadEdison')),
          filePath: 'LeadEdisonSale',
        }
      case 'leadEnel':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadEnel')),
          filePath: 'LeadEnelSale',
        }
      case 'leadSorgenia':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadSorgenia')),
          filePath: 'LeadSorgeniaSale',
        }
      case 'leadEmoovz':
        return {
          title: getSaleTab(key, getSaleIcon('sales-leadEmoovz')),
          filePath: 'LeadEmoovzSale',
        }

      default:
        if (import.meta.env.PROD) {
          Sentry.captureException(new Error('Additional sale component not found for key: ' + key))
        }
        return {
          title: t('additionalSales:common.component_not_found'),
          filePath: '',
        }
    }
  }

  useEffect(() => {
    if (!id) return
    window.Echo.channel(`additional-sale.${id}`).listen('.additional-sale.status', e => {
      const {
        zoho_contract_detail_id: zohoContractDetailId,
        zoho_contract_id: zohoContractId,
        status_code: statusCode,
        error_partner_sync: errorPartnerSync,
        sale_type: saleType,
        partner_id: partnerId,
        status_description: statusDescription,
        attributes,
      } = e

      const leadStatusData = {
        actionsInProgress: false,
        progressBarValue: 0,
        state: t('additionalSales:callback_state.end'),
      }
      let isFinished = false
      const dataToUpdate = {
        statusCode,
        errorPartnerSync,
        partnerId,
        statusDescription,
        zohoContractId,
      }

      if (attributes) {
        dataToUpdate.attributes = attributes
      }

      if (zohoContractDetailId) {
        dataToUpdate.zohoContractDetailId = zohoContractDetailId
        dataToUpdate.zohoId = zohoContractDetailId
        isFinished = true
      }

      const finalData = {
        ...dataToUpdate,
        ...(isFinished ? leadStatusData : {}),
      }

      updateLead(finalData, saleType)

      return () => {
        window.Echo.leaveChannel(`additional-sale.${id}`)
      }
    })
  }, [id, applicationType])

  useEffect(() => {
    if (tabToShow) {
      setActiveTab(tabToShow)
    }
    if (shouldScroll) {
      window.scrollTo({ behavior: 'smooth', top: containerRef.current.offsetTop })
    }
  }, [tabToShow, shouldScroll, setActiveTab, containerRef])

  return {
    t,
    getSaleConfig,
    selectedSales,
    hideComponent,
    vaPrioritiesKeys,
    leadPrioritiesKeys,
    othersKeys,
    activeTab,
    setActiveTab,
    containerRef,
    isAvailableAdditionalSale,
    hasZohoIdAdditionalSale,
    showLeadPriorities,
    setShowLeadPriorities,
    showVaPriorities,
    setShowVaPriorities,
    showOthers,
    setShowOthers,
    renderTabTitle,
  }
}

export default useAdditionalSalesContainer
